/**
 * Regola pura "conferma legata ai documenti" dello scambio voucher
 * (speculare a backend/utils/voucherConfermaGate.js) + testi di banner e badge.
 *
 * Stepper: Prenotato -> PDF Voucher -> PDF Ricevuta -> Conferme -> Completato.
 * - prenotante: conferma "di aver ricevuto il voucher" -> serve il PDF voucher
 * - proprietario: conferma "di aver ricevuto il rimborso" -> serve la ricevuta,
 *   salvo dichiarazione esplicita di rimborso ricevuto in altro modo
 */

export type RuoloScambio = 'proprietario' | 'prenotante';

export interface StatoConferma {
  stato: string;
  voucherPDF: boolean;
  ricevutaPDF: boolean;
  tuHaConfermato: boolean;
  controparteHaConfermato: boolean;
  scadenza?: string;
}

export interface EsitoConferma {
  /** Il bottone "Conferma Scambio" e' abilitato */
  puoConfermare: boolean;
  /** Il proprietario puo' dichiarare "rimborso ricevuto in altro modo" (senza ricevuta) */
  puoConfermareSenzaRicevuta: boolean;
  /** Testo del banner "cosa fare ora" */
  banner: string;
  /** Tono del banner: azione richiesta (amber) o attesa (blue) */
  bannerTono: 'azione' | 'attesa';
  /** Etichetta del badge sotto il proprio nominativo */
  badge: string;
}

function formatScadenza(scadenza?: string): string {
  if (!scadenza) return '';
  const d = new Date(scadenza);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function valutaConfermaScambio(ruolo: RuoloScambio, s: StatoConferma): EsitoConferma {
  const scad = formatScadenza(s.scadenza);
  const entro = scad ? ` entro il ${scad}` : '';
  const scadenzaInfo = scad ? ` — scadenza prenotazione: ${scad}` : '';

  if (s.stato !== 'prenotato') {
    return { puoConfermare: false, puoConfermareSenzaRicevuta: false, banner: '', bannerTono: 'attesa', badge: '' };
  }

  if (s.tuHaConfermato) {
    return {
      puoConfermare: false,
      puoConfermareSenzaRicevuta: false,
      banner: s.controparteHaConfermato
        ? '✅ Scambio completato con successo!'
        : '✅ Hai confermato. In attesa della conferma della controparte.',
      bannerTono: 'attesa',
      badge: 'Confermato'
    };
  }

  if (ruolo === 'prenotante') {
    if (!s.voucherPDF) {
      return {
        puoConfermare: false,
        puoConfermareSenzaRicevuta: false,
        banner: `📄 In attesa che il proprietario carichi il PDF del voucher${scadenzaInfo}. Potrai confermare dopo averlo ricevuto.`,
        bannerTono: 'attesa',
        badge: 'In attesa del voucher'
      };
    }
    const pagamento = s.ricevutaPDF
      ? ''
      : ' Ricorda di effettuare il pagamento e caricare la ricevuta.';
    return {
      puoConfermare: true,
      puoConfermareSenzaRicevuta: false,
      banner: `👉 Il PDF del voucher è disponibile: scaricalo e conferma la ricezione${entro}.${pagamento}`,
      bannerTono: 'azione',
      badge: 'Devi confermare'
    };
  }

  // proprietario
  if (!s.voucherPDF) {
    return {
      puoConfermare: false,
      puoConfermareSenzaRicevuta: false,
      banner: `📄 Carica il PDF del voucher per il prenotante${scadenzaInfo}.`,
      bannerTono: 'azione',
      badge: 'Devi caricare il voucher'
    };
  }
  if (!s.ricevutaPDF) {
    return {
      puoConfermare: false,
      puoConfermareSenzaRicevuta: true,
      banner: `⏳ Hai caricato il voucher. Ora il prenotante deve effettuare il pagamento e caricare la ricevuta${scadenzaInfo}. Conferma solo dopo aver ricevuto il rimborso.`,
      bannerTono: 'attesa',
      badge: 'In attesa del pagamento'
    };
  }
  return {
    puoConfermare: true,
    puoConfermareSenzaRicevuta: false,
    banner: `👉 La ricevuta di pagamento è stata caricata: verifica l'accredito e conferma lo scambio${entro}.`,
    bannerTono: 'azione',
    badge: 'Devi confermare'
  };
}
